import { useCallback, useEffect, useState, type RefObject } from 'react'

/**
 * WebKit's own fullscreen entry point lives on the video element, not on an
 * arbitrary element, and it reports state through prefixed events instead of
 * `fullscreenchange`. iPhone Safari went years with this as the *only* way in,
 * and WKWebViews still do — so even where `Element.requestFullscreen` exists,
 * the request can be refused and this is the working path.
 */
type WebkitFullscreenVideo = HTMLVideoElement & {
  webkitEnterFullscreen?: () => void
  webkitExitFullscreen?: () => void
  webkitDisplayingFullscreen?: boolean
  webkitSupportsFullscreen?: boolean
}

/** `'element'` fullscreens the container; `'webkit-video'` fullscreens the video itself. */
export type FullscreenStrategy = 'element' | 'webkit-video'

/**
 * Whether the video can enter WebKit's native fullscreen *right now*.
 *
 * `webkitSupportsFullscreen` is the live answer, and it is `false` until the
 * video has metadata — calling `webkitEnterFullscreen()` before then throws
 * `InvalidStateError`. Treating an absent flag as supported keeps every
 * non-WebKit engine (which exposes neither) on its normal path.
 */
function canEnterWebkitVideoFullscreen(video: HTMLVideoElement | null | undefined): boolean {
  const webkit = video as WebkitFullscreenVideo | null | undefined
  return typeof webkit?.webkitEnterFullscreen === 'function' && webkit.webkitSupportsFullscreen !== false
}

/**
 * Picks the fullscreen API actually available for this container/video pair, or
 * `null` when neither is usable — callers hide the control in that case rather
 * than presenting a dead button.
 *
 * Re-evaluate when the video's metadata arrives: the WebKit path only becomes
 * available then.
 */
export function selectFullscreenStrategy(
  container: Element | null | undefined,
  video: HTMLVideoElement | null | undefined,
  doc: Document = document,
): FullscreenStrategy | null {
  // `fullscreenEnabled === false` means a permissions policy has switched
  // element fullscreen off, so the API exists but every request would reject.
  if (container && typeof container.requestFullscreen === 'function' && doc.fullscreenEnabled !== false) {
    return 'element'
  }
  if (canEnterWebkitVideoFullscreen(video)) {
    return 'webkit-video'
  }
  return null
}

// A browser may refuse a fullscreen request (no user gesture, permissions
// policy). That's a no-op for us, not an error worth reporting — but it must
// not surface as an unhandled rejection.
function ignoreRejection(result: unknown): void {
  void Promise.resolve(result).catch(() => {})
}

/**
 * Enters WebKit's video fullscreen if the video can take it. The call is
 * synchronous and throws `InvalidStateError` when the video isn't ready, which
 * is a no-op for us rather than an error for the user.
 */
function enterWebkitVideoFullscreen(video: HTMLVideoElement | null | undefined): void {
  if (!canEnterWebkitVideoFullscreen(video)) return
  try {
    ;(video as WebkitFullscreenVideo).webkitEnterFullscreen?.()
  } catch {
    // Nothing to recover: the player stays inline and usable.
  }
}

/**
 * Fullscreen toggling for a video player, over whichever API the browser has.
 * Returns `strategy: null` when there is none, so the caller can drop the control.
 */
export function useVideoFullscreen(
  containerRef: RefObject<HTMLElement | null>,
  videoRef: RefObject<HTMLVideoElement | null>,
) {
  const [strategy, setStrategy] = useState<FullscreenStrategy | null>(null)
  const [isFullscreen, setIsFullscreen] = useState(false)

  useEffect(() => {
    const container = containerRef.current
    const video = videoRef.current
    const cleanups: Array<() => void> = []

    const recompute = () => setStrategy(selectFullscreenStrategy(container, video))
    recompute()

    if (video) {
      // The WebKit path only becomes usable once metadata lands.
      video.addEventListener('loadedmetadata', recompute)
      cleanups.push(() => video.removeEventListener('loadedmetadata', recompute))

      // Attached whenever the API exists, not only under the WebKit strategy: a
      // refused element request falls back to this path, and its state arrives
      // on these events rather than `fullscreenchange`.
      if (typeof (video as WebkitFullscreenVideo).webkitEnterFullscreen === 'function') {
        const enter = () => setIsFullscreen(true)
        const exit = () => setIsFullscreen(false)
        video.addEventListener('webkitbeginfullscreen', enter)
        video.addEventListener('webkitendfullscreen', exit)
        cleanups.push(() => {
          video.removeEventListener('webkitbeginfullscreen', enter)
          video.removeEventListener('webkitendfullscreen', exit)
        })
      }
    }

    if (container && typeof container.requestFullscreen === 'function') {
      const handleChange = () => setIsFullscreen(document.fullscreenElement === container)
      document.addEventListener('fullscreenchange', handleChange)
      cleanups.push(() => document.removeEventListener('fullscreenchange', handleChange))
    }

    return () => cleanups.forEach(off => off())
  }, [containerRef, videoRef])

  const toggleFullscreen = useCallback(() => {
    if (strategy === 'element') {
      const container = containerRef.current
      if (!container) return
      if (document.fullscreenElement === container) {
        ignoreRejection(document.exitFullscreen())
      } else {
        // Advertising element fullscreen isn't the same as granting it: iOS
        // builds that only let a `<video>` enter, and WKWebViews, reject here.
        // Fall back to the video's own fullscreen instead of a dead button.
        void Promise.resolve(container.requestFullscreen()).catch(() => {
          enterWebkitVideoFullscreen(videoRef.current)
        })
      }
      return
    }

    if (strategy === 'webkit-video') {
      const video = videoRef.current as WebkitFullscreenVideo | null
      if (!video) return
      if (video.webkitDisplayingFullscreen) {
        try {
          video.webkitExitFullscreen?.()
        } catch {
          // Same contract as a refused request: a no-op, not a user-facing error.
        }
        return
      }
      enterWebkitVideoFullscreen(video)
    }
  }, [strategy, containerRef, videoRef])

  return { strategy, isFullscreen, toggleFullscreen }
}
